'use client'

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdAdd,
    MdSearch,
    MdLocalPharmacy,
    MdClose,
    MdFilterList,
    MdWarning,
    MdRefresh,
    MdPlayArrow,
    MdCheck,
    MdCancel,
} from 'react-icons/md'
import {
    getDispensations,
    getDispensationQueue,
    separateDispensation,
    checkDispensation,
    cancelDispensation,
    Dispensation,
    DispensationQueueItem,
    DispensationFilters,
    DISPENSATION_STATUS_LABELS,
    DISPENSATION_STATUS_COLORS,
} from '@/lib/api/stock/dispensation-api'

export default function DispensationPage() {
    const navigate = useNavigate()
    const [dispensations, setDispensations] = useState<Dispensation[]>([])
    const [queue, setQueue] = useState<DispensationQueueItem[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [filters, setFilters] = useState<DispensationFilters>({})
    const [showFilters, setShowFilters] = useState(false)
    const [activeTab, setActiveTab] = useState<'list' | 'queue'>('list')
    const [processing, setProcessing] = useState<number | null>(null)

    useEffect(() => {
        loadData()
    }, [filters])

    const loadData = async () => {
        setLoading(true)
        try {
            const [dispResponse, queueResponse] = await Promise.all([
                getDispensations(filters),
                getDispensationQueue(),
            ])

            if (dispResponse.success) {
                setDispensations(dispResponse.result)
            }
            if (queueResponse.success) {
                setQueue(queueResponse.result)
            }
        } catch (error) {
            toast.error('Erro ao carregar dispensações')
        } finally {
            setLoading(false)
        }
    }

    const handleSeparate = async (id: number) => {
        setProcessing(id)
        try {
            const response = await separateDispensation(id)
            if (response.success) {
                toast.success('Medicamento separado')
                loadData()
            } else {
                toast.error(response.error || 'Erro ao separar')
            }
        } catch (error) {
            toast.error('Erro ao separar')
        } finally {
            setProcessing(null)
        }
    }

    const handleCheck = async (id: number) => {
        setProcessing(id)
        try {
            const response = await checkDispensation(id)
            if (response.success) {
                toast.success('Medicamento conferido')
                loadData()
            } else {
                toast.error(response.error || 'Erro ao conferir')
            }
        } catch (error) {
            toast.error('Erro ao conferir')
        } finally {
            setProcessing(null)
        }
    }

    const handleCancel = async (id: number) => {
        const reason = prompt('Motivo do cancelamento:')
        if (!reason) return

        setProcessing(id)
        try {
            const response = await cancelDispensation(id, reason)
            if (response.success) {
                toast.success('Dispensação cancelada')
                loadData()
            } else {
                toast.error(response.error || 'Erro ao cancelar')
            }
        } catch (error) {
            toast.error('Erro ao cancelar')
        } finally {
            setProcessing(null)
        }
    }

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('pt-BR')
    }

    const filteredDispensations = searchTerm
        ? dispensations.filter(
              d =>
                  d.medication?.name
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase()) ||
                  d.client?.name
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase())
          )
        : dispensations

    const pendingCount = dispensations.filter(d =>
        ['DISP_PENDING', 'DISP_SEPARATED', 'DISP_CHECKED'].includes(d.status)
    ).length

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando dispensações...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent to-accent/70 flex items-center justify-center shadow-lg">
                        <MdLocalPharmacy className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Dispensação
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {pendingCount} pendentes | {queue.length} na fila
                        </p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button
                        className="btn btn-ghost btn-sm gap-2"
                        onClick={loadData}
                    >
                        <MdRefresh className="text-lg" />
                        Atualizar
                    </button>
                    <button
                        className="btn btn-secondary gap-2"
                        onClick={() => navigate('/stock/dispensation/new')}
                    >
                        <MdAdd className="text-xl" />
                        <span className="hidden sm:inline">Nova</span>
                    </button>
                </div>
            </div>

            <div className="tabs tabs-boxed bg-base-100 p-1">
                <button
                    className={`tab ${activeTab === 'list' ? 'tab-active' : ''}`}
                    onClick={() => setActiveTab('list')}
                >
                    Lista ({dispensations.length})
                </button>
                <button
                    className={`tab ${activeTab === 'queue' ? 'tab-active' : ''}`}
                    onClick={() => setActiveTab('queue')}
                >
                    Fila ({queue.length})
                </button>
            </div>

            {activeTab === 'list' && (
                <>
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                            <div className="flex-1 relative">
                                <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                                <input
                                    type="text"
                                    placeholder="Buscar por medicamento ou paciente..."
                                    className="input input-bordered w-full pl-10"
                                    value={searchTerm}
                                    onChange={e =>
                                        setSearchTerm(e.target.value)
                                    }
                                />
                            </div>
                            {searchTerm && (
                                <button
                                    type="button"
                                    onClick={() => setSearchTerm('')}
                                    className="btn btn-ghost btn-sm"
                                >
                                    <MdClose className="text-lg" />
                                </button>
                            )}
                            <button
                                className="btn btn-outline gap-2"
                                onClick={() => setShowFilters(!showFilters)}
                            >
                                <MdFilterList className="text-lg" />
                                Filtros
                            </button>
                        </div>

                        {showFilters && (
                            <div className="mt-4 pt-4 border-t border-base-300/50 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">
                                            Status
                                        </span>
                                    </label>
                                    <select
                                        className="select select-bordered"
                                        value={filters.status || ''}
                                        onChange={e =>
                                            setFilters({
                                                ...filters,
                                                status:
                                                    (e.target.value as any) ||
                                                    undefined,
                                            })
                                        }
                                    >
                                        <option value="">Todos</option>
                                        {Object.entries(
                                            DISPENSATION_STATUS_LABELS
                                        ).map(([key, label]) => (
                                            <option key={key} value={key}>
                                                {label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="overflow-x-auto bg-base-100 rounded-xl border border-base-300/50">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Medicamento</th>
                                    <th>Paciente</th>
                                    <th>Quantidade</th>
                                    <th>Status</th>
                                    <th>Data</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredDispensations.map(disp => (
                                    <tr
                                        key={disp.id}
                                        className="hover cursor-pointer"
                                        onClick={() =>
                                            navigate(
                                                `/stock/dispensation/${disp.id}`
                                            )
                                        }
                                    >
                                        <td>
                                            <div className="flex items-center gap-2">
                                                {disp.alerts &&
                                                    disp.alerts.length > 0 &&
                                                    !disp.alertsAcknowledged && (
                                                        <MdWarning className="text-warning" />
                                                    )}
                                                <div>
                                                    <div className="font-semibold">
                                                        {disp.medication?.name}
                                                    </div>
                                                    {disp.medication
                                                        ?.genericName && (
                                                        <div className="text-sm text-base-content/60">
                                                            {
                                                                disp.medication
                                                                    .genericName
                                                            }
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td>{disp.client?.name}</td>
                                        <td>
                                            <div>
                                                {disp.dispensedQuantity}/
                                                {disp.prescribedQuantity}
                                            </div>
                                        </td>
                                        <td>
                                            <span
                                                className={`badge ${DISPENSATION_STATUS_COLORS[disp.status]}`}
                                            >
                                                {
                                                    DISPENSATION_STATUS_LABELS[
                                                        disp.status
                                                    ]
                                                }
                                            </span>
                                        </td>
                                        <td>{formatDate(disp.createdAt)}</td>
                                        <td>
                                            <div
                                                className="flex items-center gap-1"
                                                onClick={e =>
                                                    e.stopPropagation()
                                                }
                                            >
                                                {disp.status ===
                                                    'DISP_PENDING' && (
                                                    <button
                                                        className="btn btn-ghost btn-sm text-info"
                                                        onClick={() =>
                                                            handleSeparate(
                                                                disp.id
                                                            )
                                                        }
                                                        disabled={
                                                            processing ===
                                                            disp.id
                                                        }
                                                        title="Separar"
                                                    >
                                                        {processing ===
                                                        disp.id ? (
                                                            <span className="loading loading-spinner loading-sm"></span>
                                                        ) : (
                                                            <MdPlayArrow className="text-lg" />
                                                        )}
                                                    </button>
                                                )}
                                                {disp.status ===
                                                    'DISP_SEPARATED' && (
                                                    <button
                                                        className="btn btn-ghost btn-sm text-success"
                                                        onClick={() =>
                                                            handleCheck(disp.id)
                                                        }
                                                        disabled={
                                                            processing ===
                                                            disp.id
                                                        }
                                                        title="Conferir"
                                                    >
                                                        {processing ===
                                                        disp.id ? (
                                                            <span className="loading loading-spinner loading-sm"></span>
                                                        ) : (
                                                            <MdCheck className="text-lg" />
                                                        )}
                                                    </button>
                                                )}
                                                {[
                                                    'DISP_PENDING',
                                                    'DISP_SEPARATED',
                                                    'DISP_CHECKED',
                                                ].includes(disp.status) && (
                                                    <button
                                                        className="btn btn-ghost btn-sm text-error"
                                                        onClick={() =>
                                                            handleCancel(
                                                                disp.id
                                                            )
                                                        }
                                                        disabled={
                                                            processing ===
                                                            disp.id
                                                        }
                                                        title="Cancelar"
                                                    >
                                                        <MdCancel className="text-lg" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {filteredDispensations.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={6}
                                            className="text-center py-8 text-base-content/60"
                                        >
                                            Nenhuma dispensação encontrada
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {activeTab === 'queue' && (
                <div className="bg-base-100 rounded-xl border border-base-300/50">
                    {queue.length === 0 ? (
                        <div className="flex flex-col items-center justify-center min-h-[300px] gap-4 p-8">
                            <MdLocalPharmacy className="text-6xl text-base-content/30" />
                            <div className="text-center">
                                <h3 className="text-lg font-semibold text-base-content mb-1">
                                    Fila vazia
                                </h3>
                                <p className="text-sm text-base-content/60">
                                    Nenhum paciente aguardando
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Posição</th>
                                        <th>Paciente</th>
                                        <th>Prioridade</th>
                                        <th>Status</th>
                                        <th>Aguardando desde...</th>
                                        <th>Atribuído a</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {queue.map((item, index) => (
                                        <tr key={item.id}>
                                            <td className="font-bold">
                                                #{index + 1}
                                            </td>
                                            <td>{item.client?.name}</td>
                                            <td>
                                                <span
                                                    className={`badge ${item.priority > 5 ? 'badge-error' : item.priority > 3 ? 'badge-warning' : 'badge-ghost'}`}
                                                >
                                                    {item.priority}
                                                </span>
                                            </td>
                                            <td>
                                                <span
                                                    className={`badge ${item.status === 'IN_PROGRESS' ? 'badge-info' : 'badge-ghost'}`}
                                                >
                                                    {item.status === 'WAITING'
                                                        ? 'Aguardando'
                                                        : 'Em andamento'}
                                                </span>
                                            </td>
                                            <td>
                                                {formatDate(item.requestedAt)}
                                            </td>
                                            <td>
                                                {item.assignedTo?.name || '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
